import React, { FunctionComponent, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import DualPageLayout from "@components/layouts/DualPageLayout";
import ResourceFormField from "@components/inputs/ResourceFormField";
import { Collapse, Form, Input, Spin } from "antd";
import {
  ResourceInputList,
  ResourceInputSection,
} from "@type/estimateInput/estimateInput.response";
import {
  ContactDto,
  ResourceInput,
  ResourceInputDto,
  ResourceInputGroup,
  InputField,
} from "@type/estimateInput/estimateInput.dto";
import ResourceOption from "@components/inputs/ResourceOption";
import BasicButton from "@components/buttons/BasicButton";
import Results from "@components/result/Results";
import { EstimateResultsResponseDto } from "@type/estimateResults/estimateResults.response";
import Stepper from "@components/stepper/Stepper";
import FarmerResources from "src/static/data/example/farmer-resources.json";
import BasicDivider from "@components/divider/BasicDivider";
import {
  ShareAltOutlined,
  DownloadOutlined,
  LeftOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import BasicCard from "@components/cards/BasicCard";
import { ReactComponent as Wheat } from "src/static/images/wheat.svg";
import { ReactComponent as Leaf } from "src/static/images/leaf.svg";
import { ReactComponent as Cow } from "src/static/images/cow.svg";
import { ReactComponent as Waste } from "src/static/images/waste.svg";
import { ReactComponent as ProductionFarmer } from "../../static/images/production-farmer.svg";
import { useTranslation } from "react-i18next";
import { useViewport } from "@utils/breakpoints-helper";
import ShareDrawer from "@components/drawers/ShareDrawer";
import SaveDrawer from "@components/drawers/SaveDrawer";
import {
  requestFarmerComputeSimulation,
  requestFarmerSaveSimulation,
  requestLoadFarmerSimulation,
} from "@state/em_simulation/SimulationEffects";
import { requestGetFarmerResources } from "@state/em_results/EMResourceEffects";
import { toastError, toastInfo } from "@utils/toast-helper";
import { FieldData } from "rc-field-form/lib/interface";
import HTMLReactParser from "html-react-parser";
import { getCorrectSiteKey } from "@utils/captcha-helper";
import {GoogleReCaptchaProvider} from "@utils/react-google-recaptcha-v3/google-recaptcha-provider";

const { Panel } = Collapse;

const FarmerScreen: FunctionComponent<RouteComponentProps> = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [phase, setPhase] = useState<number>(0);
  const location = useLocation();
  const [currentSimulationId, setCurrentSimulationId] = useState<string>();
  const [activeSide, setActiveSide] = useState<"left" | "right">("left");
  const [currentInputGroup, setCurrentInputGroup] = useState<number>(0);
  const [resourceSections, setResourceSections] = useState<
    ResourceInputSection[]
  >([]);
  const [inputList, setInputList] = useState<ResourceInputGroup[]>([[]]);
  const [listNames, setListNames] = useState<string[]>(["Mes déchets"]);
  const [isRenaming, setIsRenaming] = useState<boolean>(false);
  const [renamingValue, setRenamingValue] = useState<string>();
  const [results, setResults] = useState<EstimateResultsResponseDto>();
  const { t } = useTranslation();
  const { width } = useViewport();
  const [isShareDrawerVisible, setIsShareDrawerVisible] =
    useState<boolean>(false);
  const [isSaveDrawerVisible, setIsSaveDrawerVisible] =
    useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [delayedComputeFn, setDelayedComputeFn] = useState<
    NodeJS.Timeout | undefined
  >(undefined);
  const [bioWasteToastShown, setBioWasteToastShown] = useState<boolean>(false);
  const [inputGroupsValid, setInputGroupsValid] = useState<boolean>(false);

  useEffect(() => {
    void requestGetFarmerResources({}).then((result) => {
      if (result.ok && result.data) {
        const resourceList = result.data;
        if ((FarmerResources as ResourceInputList).type === "farmer") {
          setResourceSections(resourceList.sections);
          setIsLoading(false);
        } else {
          // Error toast ?
        }
      }
    });
  }, []);

  useEffect(() => {
    const simulationId = new URLSearchParams(location.search).get("id");
    if (simulationId) {
      void requestLoadFarmerSimulation({ id: simulationId }).then((result) => {
        if (result.ok && result.data) {
          if (result.data.result?.type === "FARMER") {
            if (result.data.inputs) {
              const inputListFromDto = buildInputListFromDto(
                result.data.inputs
              );
              setInputList(inputListFromDto);
              const inputValues: FieldData[] = [];
              inputListFromDto.forEach((inputGroup, index) => {
                inputGroup.forEach((input) => {
                  inputValues.push({
                    name: `${input.key}-group${index}`,
                    value: {
                      value: input.value,
                      unit: input.unit,
                    } as InputField,
                  });
                });
              });
              form.setFields(inputValues);
              setPhase(1);
            }
            if (result.data.result) {
              handleResults(result.data.result);
              setInputGroupsValid(true);
            }
          } else {
            toastError(
              "Une erreur est survenue lors de la récupération de votre simulation."
            );
          }
        }
      });
    }
  }, [location]);

  const clearEmptyInputGroups = (): ResourceInputGroup[] => {
    const filteredInputGroups = inputList;
    const filteredInputGroupNames = listNames;
    inputList.forEach((group, index) => {
      if (!group.length) {
        filteredInputGroups.splice(index, 1);
        filteredInputGroupNames.splice(index, 1);
      }
    });
    setInputList(filteredInputGroups);
    setListNames(filteredInputGroupNames);
    return filteredInputGroups;
  };

  const preparePhase1 = () => {
    const filteredInputGroups = clearEmptyInputGroups();
    if (isValid(filteredInputGroups)) {
      computeResults();
    }
    setPhase(1);
  };

  const getLeftFooterActions = (phase: number) => {
    if (phase === 0) {
      return (
        <div className="side-panel-footer-content d-flex flex-row justify-content-between align-items-center">
          <a
            className="button-link text-sm font-weight-bold text-decoration-none d-flex align-items-center p-0 me-2"
            onClick={history.goBack}
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                history.goBack();
              }
            }}
          >
            <LeftOutlined />
            <div className="ms-2">Retour</div>
          </a>
          <BasicButton
            className={"button-icon"}
            variant={
              !inputList.some((list) => list.length) ? "disabled" : "primary"
            }
            onClick={() => {
              if (!inputList.some((list) => list.length)) {
                toastError(t("results.toasts.errors.select-one"));
              } else {
                preparePhase1();
              }
            }}
            text={"Valider mes déchets"}
          />
        </div>
      );
    } else if (phase === 1 && width < 768) {
      return (
        <div className="side-panel-footer-content d-flex flex-row justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <ProductionFarmer className="icon-black icon-results" />
            <div className="ms-2">
              <div className="text-sm font-weight-bold mb-1">
                {results && results.synthesis
                  ? results.synthesis.methaneProduction.value[0].value ?? ""
                  : t("results.synthesis.farmer.production.methaneValue")}{" "}
                {results && results.synthesis
                  ? HTMLReactParser(
                      t(
                        `result_units.${results.synthesis.methaneProduction.value[0].unit}`,
                        results.synthesis.methaneProduction.value[0].unit
                      )
                    ) ?? ""
                  : t("results.synthesis.farmer.production.methaneUnit")}
              </div>
              <div className="text-sm">
                {t("results.synthesis.farmer.production.label")}
              </div>
            </div>
          </div>
          <BasicButton
            className="button-icon ms-2"
            variant={results ? "secondary" : "disabled"}
            onClick={() => {
              if (results && activeSide === "left") setActiveSide("right");
              else toastError(t("results.toasts.errors.incomplete"));
            }}
            text="Voir les résultats"
          />
        </div>
      );
    }
    return undefined;
  };

  const getHeaderActions = () => {
    if (!results) return undefined;
    return (
      <div className="d-flex flex-row align-items-center justify-content-center justify-content-md-end w-100">
        <div className="d-flex flex-row align-items-center d-md-none">
          {activeSide === "right" && (
            <BasicButton
              className="button-icon collapsible-button-xs"
              variant="white"
              onClick={() => {
                setActiveSide("left");
              }}
              text={"Modifier la simulation"}
              icon={<LeftOutlined />}
            />
          )}
          <BasicDivider variant="grey" width={1} height={32} className="mx-3" />
        </div>
        <BasicButton
          className="button-icon collapsible-button me-3"
          variant="white"
          onClick={() => setIsShareDrawerVisible(true)}
          text={"Partager votre simulation"}
          icon={<ShareAltOutlined aria-hidden="true" />}
          size="lg"
        />
        <BasicButton
          className="button-icon collapsible-button"
          variant="secondary"
          onClick={() => setIsSaveDrawerVisible(true)}
          text={"Enregistrer votre simulation"}
          icon={<DownloadOutlined aria-hidden="true" />}
          size="lg"
        />
        <GoogleReCaptchaProvider
          reCaptchaKey={getCorrectSiteKey()}
          language="fr"
          scriptProps={{
            async: false, // optional, default to false,
            defer: false, // optional, default to false
            appendTo: "head", // optional, default to "head", can be "head" or "body",
            nonce: undefined, // optional, default undefined
          }}
        >
          <ShareDrawer
            source={"FARMER"}
            visible={isShareDrawerVisible}
            simulationId={currentSimulationId}
            saveSimulation={handleSaveSimulationWithContactDto}
            onUpdateSimulationId={setCurrentSimulationId}
            onClose={() => setIsShareDrawerVisible(false)}
          />
          <SaveDrawer
            isCollectivity={false}
            visible={isSaveDrawerVisible}
            saveSimulation={handleSaveSimulationWithContactDto}
            onUpdateSimulationId={setCurrentSimulationId}
            onClose={() => setIsSaveDrawerVisible(false)}
          />
        </GoogleReCaptchaProvider>
      </div>
    );
  };

  const getResourceByKey = (key: string) => {
    for (const section of resourceSections) {
      for (const resource of section.content) {
        if (resource.key === key) return resource;
      }
    }
    return null;
  };

  const findIndexInInputList = (key: string, groupIndex: number) => {
    return inputList[groupIndex]?.findIndex((input) => input.key === key);
  };
  const isResourceSelected = (key: string, groupIndex: number) => {
    return findIndexInInputList(key, groupIndex) !== -1;
  };
  const getNumberResourcesSelected = (
    section: ResourceInputSection,
    groupIndex: number
  ) => {
    let resourcesSelected = 0;
    section.content.forEach((resource) => {
      if (isResourceSelected(resource.key, groupIndex)) resourcesSelected++;
    });
    return resourcesSelected;
  };

  const handleAddResource = (key: string, section: number) => {
    if (section === 4 && !bioWasteToastShown) {
      toastInfo(t("results.toasts.info.bio-waste"));
      setBioWasteToastShown(true);
    }

    if (!isResourceSelected(key, currentInputGroup)) {
      const newInput = {
        key,
        value: undefined,
        unit: undefined,
      };
      const newInputList: ResourceInputGroup[] = inputList.map(
        (group, index) => {
          if (index === currentInputGroup) return [...group, newInput];
          else return group;
        }
      );
      setInputList(newInputList);
    }
  };

  const handleRemoveResource = (key: string) => {
    if (isResourceSelected(key, currentInputGroup)) {
      const newInputList: ResourceInputGroup[] = inputList.map(
        (group, index) => {
          if (index === currentInputGroup) {
            const array = group; // make a separate copy of the array
            const i = findIndexInInputList(key, index);
            if (i !== -1) {
              array.splice(i, 1);
              return array;
            } else {
              return group;
            }
          } else return group;
        }
      );
      setInputList(newInputList);
    }
  };

  const handleSaveSimulationWithContactDto = (
    contactDto: ContactDto,
    successCallback: (simulationId: string) => void,
    errorCallback: () => void
  ) => {
    const inputDto = buildInputDto();
    const simulationSaveDto = { inputs: inputDto, contact: contactDto };

    requestFarmerSaveSimulation({ dto: simulationSaveDto })
      .then((result) => {
        if (result && result.ok) {
          if (result.data) {
            successCallback(result.data.id);
          } else {
            errorCallback();
            toastError(
              "Une erreur est survenue lors de la sauvegarde de votre simulation"
            );
          }
        } else {
          errorCallback();
          toastError(
            "Une erreur est survenue lors de la sauvegarde de votre simulation"
          );
        }
      })
      .catch(() => {
        errorCallback();
        toastError(
          "Une erreur est survenue lors de la sauvegarde de votre simulation"
        );
      });
  };

  const renderIcon = (name: string) => {
    switch (name) {
      case "wheat":
        return <Wheat aria-hidden="true" focusable="false" />;
      case "leaf":
        return <Leaf aria-hidden="true" focusable="false" />;
      case "cow":
        return <Cow aria-hidden="true" focusable="false" />;
      case "waste":
        return <Waste aria-hidden="true" focusable="false" />;
      default:
        return <></>;
    }
  };

  const renderResourcesSection = (sections: ResourceInputSection[]) => {
    return (
      <Collapse
        accordion
        expandIconPosition="right"
        className="resources-section-accordions"
      >
        {sections.map((section) => (
          <Panel
            key={`resources-section-${section.index}`}
            header={
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  {section.icon && (
                    <div className="resource-icon me-2">
                      {renderIcon(section.icon)}
                    </div>
                  )}
                  <div>{section.label}</div>
                </div>
                {getNumberResourcesSelected(section, currentInputGroup) > 0 && (
                  <div className="resource-selected-number">
                    {getNumberResourcesSelected(section, currentInputGroup)}
                  </div>
                )}
              </div>
            }
          >
            {section.content.map((resource, index) => {
              return (
                <ResourceOption
                  key={`resources-option-${resource.key}`}
                  resource={resource}
                  value={isResourceSelected(resource.key, currentInputGroup)}
                  onCheck={(key) => handleAddResource(key, section.index)}
                  onUncheck={handleRemoveResource}
                  className={index === section.content.length - 1 ? "mb-0" : ""}
                />
              );
            })}
          </Panel>
        ))}
      </Collapse>
    );
  };

  const renderResourceInputs = (groupIndex: number) =>
    inputList[groupIndex]?.map((input, index) => {
      const resource = getResourceByKey(input.key);
      if (resource)
        return (
          <React.Fragment key={`resources-input-${input.key}`}>
            <Form.Item
              required
              key={`resources-input-${input.key}-group${groupIndex}`}
              name={`${input.key}-group${groupIndex}`}
            >
              <ResourceFormField
                resource={resource}
                module={"farmer"}
                onChange={validateForm}
              />
            </Form.Item>
            {index !== inputList[groupIndex].length - 1 && (
              <BasicDivider
                variant="grey"
                height={1}
                className={"w-100 mt-4 mb-4"}
              />
            )}
          </React.Fragment>
        );
      else return <></>;
    });

  const buildInputDto = () => {
    const simulationInputsDto: ResourceInputDto[] = [];
    inputList.forEach((inputGroup, groupIndex) => {
      const simulationInputsDtoItem: ResourceInputDto = {};
      inputGroup.forEach((input) => {
        simulationInputsDtoItem[input.key] = {
          value: (
            form.getFieldValue(`${input.key}-group${groupIndex}`) as InputField
          )?.value,
          unit: (
            form.getFieldValue(`${input.key}-group${groupIndex}`) as InputField
          )?.unit,
        };
      });
      simulationInputsDtoItem["nomGroupe"] = listNames[groupIndex];
      simulationInputsDto.push(simulationInputsDtoItem);
    });
    return simulationInputsDto;
  };

  const buildInputListFromDto = (inputDtoList: ResourceInputDto[]) => {
    const newInputGroupList: ResourceInputGroup[] = [];
    const simulationGroupNames: string[] = [];

    inputDtoList.forEach((inputGroup, groupIndex) => {
      const simulationGroup: ResourceInputGroup = [];
      let groupName = defaultGroupName(groupIndex);
      Object.keys(inputGroup).forEach((key) => {
        const value = inputGroup[key];
        if (typeof value !== "string") {
          simulationGroup.push({
            key: key,
            value: value?.value,
            unit: value?.unit,
          });
        } else if (key === "nomGroupe") {
          groupName = value;
        }
      });
      newInputGroupList.push(simulationGroup);
      simulationGroupNames[groupIndex] = groupName;
    });
    if (inputDtoList.length > 1) setListNames(simulationGroupNames);
    return newInputGroupList;
  };

  const computeResults = () => {
    if (delayedComputeFn) clearTimeout(delayedComputeFn);

    const delayDebounceFn = setTimeout(() => {
      const inputsDto = buildInputDto();
      requestFarmerComputeSimulation({ dto: inputsDto });
    }, 500);
    setDelayedComputeFn(delayDebounceFn);
  };

  useEffect(() => {
    return requestFarmerComputeSimulation.done.watch(({ result }) => {
      if (result.ok && result.data) {
        handleResults(result.data);
      }
    });
  });

  const handleResults = (results: EstimateResultsResponseDto) => {
    if (!results.errors || results.errors.length === 0) {
      setResults(results);

      for (const fieldName of validationErrors) {
        form.setFields([{ name: fieldName, errors: [] }]);
      }
      setValidationErrors([]);
    } else {
      const newValidationErrorList: string[] = [];
      for (const error of results.errors) {
        const fieldName = `${error.field}-group${error.groupIndex}`;
        if (!validationErrors.includes(fieldName)) {
          toastError(error.message);
          form.setFields([{ name: fieldName, errors: [error.message] }]);
          setValidationErrors([...validationErrors, ...[fieldName]]);
        }
        newValidationErrorList.push(fieldName);
      }

      for (const error of validationErrors) {
        if (!newValidationErrorList.includes(error)) {
          form.setFields([{ name: error, errors: [] }]);
        }
      }
      setResults(undefined);
    }
  };

  const isValid = (inputList: ResourceInputGroup[]) => {
    const valid = inputList.every((inputGroup, groupIndex) =>
      inputGroup.every(
        (input) =>
          ((
            form.getFieldValue(
              `${input.key}-group${groupIndex}`
            ) as ResourceInput
          )?.value ?? -1) >= 0
      )
    );
    setInputGroupsValid(valid);
    return valid;
  };

  const validateForm = () => {
    const valid = isValid(inputList);
    if (valid) {
      computeResults();
      if (width >= 768 && activeSide !== "right") {
        setActiveSide("right");
      }
    }
  };

  const resetInputSection = () => {
    const newInputList = inputList.map((group, index) => {
      if (index === currentInputGroup) return [];
      else return group;
    });
    setInputList(newInputList);
  };

  const renameGroup = () => {
    const groupNames = listNames;
    const newName = renamingValue ?? defaultGroupName(currentInputGroup);
    groupNames[currentInputGroup] = newName;
    setListNames(groupNames);
    setIsRenaming(false);
    setRenamingValue(undefined);
  };

  const defaultGroupName = (index: number): string => {
    return `Source de déchet n°${index + 1}`;
  };

  return (
    <DualPageLayout
      container
      noHeader={width < 768 && activeSide === "left"}
      headerActions={getHeaderActions()}
      leftBodyClassName={`side-panel-farmer ${
        activeSide === "left" ? "active-panel" : ""
      }`}
      leftFooterActions={getLeftFooterActions(phase)}
      noFooter
      leftContent={
        <>
          {isLoading ? (
            <div className="h-100 py-3 d-flex align-items-center justify-content-center">
              <Spin />
            </div>
          ) : (
            phase === 0 && (
              <div className="d-flex flex-column w-100 align-items-center">
                {inputList.map((group, index) => {
                  if (index === currentInputGroup) {
                    return (
                      <BasicCard className="resources-section-card" key={index}>
                        <>
                          <div className="resources-section-card-header d-flex align-items-center justify-content-between">
                            {isRenaming ? (
                              <Input
                                value={renamingValue}
                                placeholder={listNames[currentInputGroup]}
                                onChange={(input) =>
                                  setRenamingValue(input.target.value)
                                }
                                onBlur={renameGroup}
                                onKeyPress={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    renameGroup();
                                  }
                                }}
                              />
                            ) : (
                              <>
                                <div className="resources-section-card-title">
                                  {listNames[currentInputGroup]}
                                </div>
                                {inputList.length > 1 && (
                                  <a
                                    className="resources-section-card-link"
                                    onClick={() => {
                                      setIsRenaming(true);
                                    }}
                                    tabIndex={0}
                                    onKeyPress={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setIsRenaming(true);
                                      }
                                    }}
                                  >
                                    Renommer
                                  </a>
                                )}
                              </>
                            )}
                          </div>
                          {renderResourcesSection(resourceSections)}
                          <div className="resources-section-card-footer">
                            <a
                              className="resources-section-card-link"
                              onClick={resetInputSection}
                              tabIndex={0}
                              onKeyPress={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  resetInputSection();
                                }
                              }}
                            >
                              Tout effacer
                            </a>
                          </div>
                        </>
                      </BasicCard>
                    );
                  } else {
                    return (
                      <BasicCard className="resources-section-card" key={index}>
                        <div className="resources-section-card-header d-flex align-items-center justify-content-between">
                          <div className="resources-section-card-title">
                            {listNames[index]}
                          </div>
                          <BasicButton
                            variant="primary"
                            text="Modifier"
                            className="resources-inputs-card-button ms-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentInputGroup(index);
                            }}
                          />
                        </div>
                      </BasicCard>
                    );
                  }
                })}
              </div>
            )
          )}
          {(phase === 1 || phase === 2) && (
            <div className="d-flex flex-column w-100 align-items-center">
              <BasicButton
                variant="primary-outlined"
                icon={<PlusOutlined />}
                text="Autres sources de déchets"
                className={`resources-inputs-add-button mb-3 anim-expand-height ${
                  inputGroupsValid ? "anim-open" : ""
                }`}
                onClick={() => {
                  const newGroupIndex = inputList.length;
                  setInputList([...inputList, []]);
                  setListNames([...listNames, defaultGroupName(newGroupIndex)]);
                  setCurrentInputGroup(newGroupIndex);
                  setPhase(0);
                }}
              />
              {inputList.map((group, index) => (
                <BasicCard className="resources-inputs-card" key={index}>
                  <>
                    <div className="resources-inputs-card-header d-flex align-items-center justify-content-between">
                      <div className="resources-section-card-title">
                        {listNames[index]}
                      </div>
                      <BasicButton
                        variant="primary"
                        text="Modifier"
                        className="resources-inputs-card-button ms-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPhase(0);
                          setCurrentInputGroup(index);
                        }}
                      />
                    </div>
                    <Form
                      form={form}
                      initialValues={inputList}
                      onBlur={validateForm}
                    >
                      {renderResourceInputs(index)}
                    </Form>
                  </>
                </BasicCard>
              ))}
            </div>
          )}
        </>
      }
      rightBodyClassName={`d-flex w-100 ${
        activeSide === "right" ? "active-panel" : null
      }`}
      rightContent={
        <>
          {!results && (
            <div className="px-4 px-lg-64 mb-3">
              <Stepper
                current={phase}
                steps={[
                  { title: "Vos intrants" },
                  { title: "Vos volumes" },
                  { title: "Votre potentiel" },
                ]}
              />
            </div>
          )}
          <Results
            title={"Estimation de votre potentiel de production"}
            type={"farmer"}
            results={results}
            onShare={() => {
              setIsShareDrawerVisible(true);
            }}
            onSave={() => {
              setIsSaveDrawerVisible(true);
            }}
          />
        </>
      }
    />
  );
};

export default FarmerScreen;
